import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, X, Clock, Users } from 'lucide-react';
import { getInactiveUsers } from '../../services/inactiveUsersApi';
import { useAuth } from '../../contexts/AuthContext';

// ---- Sort helpers ----

const getSortValue = (user, key) => {
  switch (key) {
    case 'FullName':
      return `${user.firstName} ${user.lastName}`.toLowerCase();
    case 'Email':
      return (user.email || '').toLowerCase();
    case 'TenantKey':
      return (user.tenantKey || '').toLowerCase();
    case 'UserType':
      return (user.userType || '').toLowerCase();
    case 'DaysInactive':
      return user.daysInactive ?? Infinity;
    case 'LastLoginUTCDateTime':
      return user.lastLoginUTCDateTime ? new Date(user.lastLoginUTCDateTime).getTime() : null;
    case 'IsActive':
      return user.isActive ? 1 : 0;
    default:
      return null;
  }
};

const sortUsers = (users, sortKey, sortDir) => {
  return [...users].sort((a, b) => {
    let aVal = getSortValue(a, sortKey);
    let bVal = getSortValue(b, sortKey);

    if (sortKey === 'LastLoginUTCDateTime') {
      // null = never logged in
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return sortDir === 'desc' ? -1 : 1;
      if (bVal === null) return sortDir === 'desc' ? 1 : -1;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'desc' ? -cmp : cmp;
    }

    if (aVal < bVal) return sortDir === 'desc' ? 1 : -1;
    if (aVal > bVal) return sortDir === 'desc' ? -1 : 1;
    return 0;
  });
};

// ---- Sub-components ----

const SortableHeader = ({ label, sortKey, currentSortKey, sortDir, onSort, className = '' }) => {
  const isActive = sortKey === currentSortKey;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        ) : (
          <ChevronsUpDown size={14} className="text-gray-300 dark:text-slate-600" />
        )}
      </span>
    </th>
  );
};

const SkeletonRow = () => (
  <tr className="border-b border-gray-100 dark:border-slate-800">
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3 animate-pulse" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2 animate-pulse" /></td>
  </tr>
);

const TableHeader = ({ sortKey, sortDir, onSort }) => (
  <thead className="bg-gray-50 dark:bg-slate-950">
    <tr>
      <SortableHeader label="Full Name" sortKey="FullName" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="Email" sortKey="Email" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="Tenant" sortKey="TenantKey" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="Last Login" sortKey="LastLoginUTCDateTime" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="Days Inactive" sortKey="DaysInactive" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="User Type" sortKey="UserType" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
      <SortableHeader label="Status" sortKey="IsActive" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
    </tr>
  </thead>
);

// ---- Main component ----

const InactiveUsers = () => {
  const { logout } = useAuth();

  // Filter/threshold state
  const [thresholdDays, setThresholdDays] = useState(90);
  const [customThreshold, setCustomThreshold] = useState('90');
  const [selectedTenant, setSelectedTenant] = useState('all');

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Sort state
  const [sortKey, setSortKey] = useState('DaysInactive');
  const [sortDir, setSortDir] = useState('desc');

  // ---- Derived data ----
  const allUsers = data ? Object.values(data.byTenant).flat() : [];
  const visibleUsers =
    selectedTenant === 'all' ? allUsers : (data?.byTenant[selectedTenant] || []);
  const uniqueTenantCount = data ? Object.keys(data.byTenant).length : 0;
  const sortedUsers = sortUsers(visibleUsers, sortKey, sortDir);

  // Tenant dropdown options
  const tenantOptions = data
    ? [
        { value: 'all', label: `All (${data.totalCount})` },
        ...Object.keys(data.byTenant).map((key) => ({
          value: key,
          label: `${key} (${data.countByTenant[key] ?? 0})`,
        })),
      ]
    : [{ value: 'all', label: 'All' }];

  // ---- Handlers ----

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handlePreset = (days) => {
    setThresholdDays(days);
    setCustomThreshold(String(days));
  };

  const handleCustomThreshold = (e) => {
    const raw = e.target.value;
    setCustomThreshold(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      setThresholdDays(parsed);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getInactiveUsers({ thresholdDays });
      setData(result);
      setWarnings(result.warnings || []);
    } catch (err) {
      if (err.status === 401) {
        logout();
      } else {
        setError(err.message || 'Failed to load inactive users');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- Preset active detection ----
  const isPresetActive = (days) => thresholdDays === days && String(days) === customThreshold;

  // ---- Render ----
  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Inactive Users</h1>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Users who haven't logged in within the threshold period
        </p>
      </div>

      {/* Filter Controls Row */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        {/* Tenant Dropdown */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
            Tenant
          </label>
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="border border-gray-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {tenantOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Threshold Controls */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
            Inactive Threshold
          </label>
          <div className="flex items-center gap-2">
            {/* Preset buttons */}
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-slate-600 divide-x divide-gray-300 dark:divide-slate-600 overflow-hidden">
              {[30, 60, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => handlePreset(days)}
                  className={`px-3 py-2 text-sm ${
                    isPresetActive(days)
                      ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>

            {/* Custom input */}
            <input
              type="number"
              min="1"
              placeholder="Custom"
              value={customThreshold}
              onChange={handleCustomThreshold}
              className="w-20 border border-gray-300 dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Apply button */}
            <button
              onClick={handleApply}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 ml-3"
            aria-label="Dismiss error"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Warnings Banner */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-amber-700 text-sm font-medium mb-1">
            Partial results -- some tenants could not be queried:
          </p>
          <ul className="list-disc list-inside text-amber-700 text-sm">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Summary count */}
      {data && !loading && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Showing {sortedUsers.length} inactive users across {uniqueTenantCount} tenants
        </p>
      )}

      {/* Initial state prompt */}
      {data === null && !loading && (
        <div className="text-center py-12">
          <Clock size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-4" />
          <p className="text-gray-400 dark:text-slate-500 text-sm">Click Apply to load inactive users</p>
        </div>
      )}

      {/* Table (shown during loading, with data, or empty) */}
      {(loading || data !== null) && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <TableHeader sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
              {/* Loading skeleton */}
              {loading &&
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

              {/* Empty state */}
              {!loading && allUsers.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="text-center py-12">
                      <Users size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-4" />
                      <p className="text-gray-500 dark:text-slate-400 text-lg font-medium">No inactive users found</p>
                      <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">
                        No users have been inactive for more than {thresholdDays} days
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {/* Data rows */}
              {!loading &&
                sortedUsers.map((user, idx) => (
                  <tr key={`${user.email}-${user.tenantKey}-${idx}`} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800">
                    {/* Full Name */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.firstName} {user.lastName}
                    </td>

                    {/* Email */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.email}</td>

                    {/* Tenant */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.tenantKey}</td>

                    {/* Last Login */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.daysInactive === null || user.lastLoginUTCDateTime === null ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          Never
                        </span>
                      ) : (
                        new Date(user.lastLoginUTCDateTime).toLocaleDateString()
                      )}
                    </td>

                    {/* Days Inactive */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.daysInactive === null ? (
                        <span className="text-gray-400 dark:text-slate-500">N/A</span>
                      ) : (
                        `${user.daysInactive} days`
                      )}
                    </td>

                    {/* User Type */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.userType
                        ? user.userType.charAt(0).toUpperCase() + user.userType.slice(1)
                        : ''}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.isActive ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InactiveUsers;
